import * as api from '@opentelemetry/api';
import { CompositePropagator, W3CBaggagePropagator, W3CTraceContextPropagator } from '@opentelemetry/core';
import { BatchSpanProcessor, TraceIdRatioBasedSampler } from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { browserDetector, detectResourcesSync, Resource } from '@opentelemetry/resources';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { B3InjectEncoding, B3Propagator } from '@opentelemetry/propagator-b3';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { generateSpoofUrl } from 'app/util/generateSpoofUrl';

let initialized = false;
let initializing;
let myTracer;
let $auth;
let sampleForced = false;
let sampleForcedReason;

// Identity must be flat primitives: nested attribute objects get dropped by the pipeline.
// Approved identity attributes are company_id and user_id only (never email).
// In spoofed sessions company_id/user_id are the ACTIVE (spoofed) identity — the company
// whose data the session views — and spoof_company_id/spoof_user_id carry the admin's own.
function getIdentityAttributes() {
  const attributes = {};
  const companyId = $auth?.getActiveUserProperty('company_id') ?? $auth?.getActiveUserProperty('company.id');
  const userId = $auth?.getActiveUserProperty('id');
  if (companyId != null) {
    attributes.company_id = companyId;
  }
  if (userId != null) {
    attributes.user_id = userId;
  }
  if ($auth?.isSpoofed && $auth.companyId != null) {
    // $auth.companyId resolves through chfAdmin when spoofed = the spoofing admin's company
    attributes.spoof_company_id = $auth.companyId;
  }
  // deployed branch (from the node server's build tag via open-config) — lets Honeycomb separate
  // ephemeral/branch traffic from production releases
  const branch = $auth?.openConfig?.branch;
  if (branch) {
    attributes.branch = branch;
  }
  return attributes;
}

function getAuthSpanAttributes() {
  if (!initialized) {
    return {};
  }
  const attributes = {
    page_url: window.location.href,
    spoof_url: generateSpoofUrl($auth),
    spoofing: $auth.isSpoofed || false,
    spoof_user_id: $auth.getActiveUserProperty('chfAdmin.id') || 0,
    ...getIdentityAttributes()
  };
  if (sampleForced) {
    // boolean, never a string: Refinery's rule matches the boolean type
    attributes.sample_forced = true;
    attributes.sample_forced_reason = sampleForcedReason;
  }
  return attributes;
}

/**
 * Marks the active span and all subsequently created spans in this session with
 * sample_forced=true, routing their traces to Refinery's larger sampling budget.
 * Call when a perf gauge crosses a "this session is worth keeping" threshold
 * (slow boot, slow keystroke, oversized collection).
 */
export function forceSampleTraces(reason = 'unspecified') {
  sampleForced = true;
  sampleForcedReason = reason;
  const span = initialized ? api.trace.getActiveSpan() : undefined;
  if (span?.isRecording()) {
    span.setAttribute('sample_forced', true);
    span.setAttribute('sample_forced_reason', reason);
  }
}

export function getSpoofUrl() {
  return initialized && $auth ? generateSpoofUrl($auth) : undefined;
}

const _initialize = async (authStore) => {
  const brResources = detectResourcesSync({ detectors: [browserDetector] });
  $auth = authStore;
  const collectorOptions = {
    url: `${window.location.origin}/api/ui/otel/v1/traces`,
    headers: {
      Accept: '*/*',
      'X-CSRF-Token': $auth.csrfToken,
      'Content-Type': 'application/octet-stream'
    }
  };

  const serviceName = 'ui-app-browser';

  const RATE = 1; // This could a config var.
  // Not down sampling to maintain previous behavior.
  const sampleRate = parseFloat(RATE);

  const provider = new WebTracerProvider({
    resource: new Resource({
      'service.name': serviceName,
      // flat primitives only — the collector gateway normalizes company id aliases to company_id
      ...getIdentityAttributes()
    }).merge(brResources),
    // Use TraceIdRatioBasedSampler to sample a percentage of traces
    sampler: new TraceIdRatioBasedSampler(sampleRate)
  });

  provider.addSpanProcessor(new BatchSpanProcessor(new OTLPTraceExporter(collectorOptions)));
  provider.register({
    contextManager: new ZoneContextManager(),
    propagator: new CompositePropagator({
      propagators: [
        new B3Propagator({ injectEncoding: B3InjectEncoding.MULTI_HEADER }),
        new W3CTraceContextPropagator(),
        new W3CBaggagePropagator()
      ]
    })
  });

  const InstrConfig = {
    ignoreUrls: ['/hc', '/api/ui/hc-full', '/api/ui//hc-full', '/api/ui/metrics/browser'],
    applyCustomAttributesOnSpan: (span) => {
      span.setAttributes(getAuthSpanAttributes());
    }
  };

  registerInstrumentations({
    instrumentations: [
      new XMLHttpRequestInstrumentation(InstrConfig),
      new FetchInstrumentation(InstrConfig),
      new DocumentLoadInstrumentation()
    ]
  });

  initialized = true;
  myTracer = api.trace.getTracer(serviceName);
};

export function initializeOpenTelemetry(authStore) {
  if (!initializing) {
    initializing = _initialize(authStore);
  }
  return initializing;
}

export const noopSpan = {
  setAttributes: () => {},
  setAttribute: () => {},
  setStatus: () => {},
  end: () => {},
  addEvent: () => {},
  isRecording: () => false,
  _spanContext: {},
  attributes: {}
};
export function getActiveSpan() {
  return initialized ? api.trace.getActiveSpan() : noopSpan;
}

export function getSpanMeta(currentSpan = {}) {
  const ctext = currentSpan._spanContext || {};
  return {
    name: currentSpan.name,
    traceId: ctext.traceId,
    spanId: ctext.spanId,
    status: currentSpan.status,
    isRecording: currentSpan.isRecording()
  };
}

export function injectB3(b3Object = {}) {
  api.propagation.inject(api.context.active(), b3Object);
  return b3Object;
}

export function activateSpan(fn, name, attributes = {}, b3 = {}) {
  if (!initialized) {
    return fn;
  }
  return (...args) =>
    myTracer.startActiveSpan(
      name || fn.name,
      {
        kind: api.SpanKind.CLIENT,
        attributes: {
          ...attributes,
          ...getAuthSpanAttributes()
        },
        startTime: Date.now()
      },
      api.propagation.extract(api.context.active(), b3),
      (currentSpan) =>
        Promise.resolve(fn(...args)).finally(() => {
          if (currentSpan.isRecording()) {
            currentSpan.end();
          }
        })
    );
}
